import { forwardRef, RefObject, useState } from 'react';
import { useIntl } from 'react-intl';
import { toLocaleDate } from '@fe-learner-common/utils/date';
import { DateTime } from 'luxon';
import { DateRange } from '@fe-learner-common';
import { DatePicker } from 'fe-core-ui/src/components/Datepicker/Datepicker';
import { getLocaleDateFormatPlaceholder } from 'fe-core-ui/src/utils/date';
import { useConfigContext } from '@fe-core';

type DateRangeChoice = {
  label: string;
  filterKey: string;
  value: DateRange;
  allowedRange?: DateRange;
  onChange: (value: DateRange) => void;
  containerRef: RefObject<HTMLDivElement>;
};

export const DateRangeChoice = forwardRef<HTMLDivElement, DateRangeChoice>(
  ({ label, filterKey, value, containerRef, allowedRange, onChange }, ref) => {
    const intl = useIntl();
    const [range, setRange] = useState<DateRange>(value);
    const { config } = useConfigContext();

    const ariaKey = `filter-group-${filterKey}`;

    return (
      <div ref={ref} role="group">
        <div className="flex items-center justify-between px-3 py-3.5">
          <h2 className="text-label-md leading-5" id={ariaKey}>
            {label}
          </h2>
        </div>
        <div className="space-y-4 px-3">
          <div className="space-y-2">
            <h3 className="text-label-sm text-neutral-500">
              {intl.formatMessage({
                id: 'learner.common.filter.date.course.start',
              })}
            </h3>
            <DatePicker
              placeholder={
                value.start
                  ? toLocaleDate(value.start, DateTime.DATE_FULL)
                  : getLocaleDateFormatPlaceholder(config.locale)
              }
              locale={config.locale}
              allowedRange={allowedRange}
              onSelect={(date) => {
                setRange({ ...range, start: date.toISOString() });
                onChange({ ...range, start: date.toISOString() });
              }}
              container={containerRef.current as HTMLElement}
            />
          </div>
          <div className="space-y-2" ref={ref}>
            <h3 className="text-label-sm text-neutral-500">
              {intl.formatMessage({
                id: 'learner.common.filter.date.course.end',
              })}
            </h3>
            <DatePicker
              placeholder={
                value.end
                  ? toLocaleDate(value.end, DateTime.DATE_FULL)
                  : getLocaleDateFormatPlaceholder(config.locale)
              }
              locale={config.locale}
              allowedRange={allowedRange}
              onSelect={(date) => {
                setRange({ ...range, end: date.toISOString() });
                onChange({ ...range, end: date.toISOString() });
              }}
              container={containerRef.current as HTMLElement}
            />
          </div>
        </div>
      </div>
    );
  },
);
DateRangeChoice.displayName = 'DateRangeChoice';
